import React, { Component } from 'react';
import cn from 'classnames';
import NavLink from 'components/Navigation/NavLink';
import withUser from 'decorators/withUser';
import {
  getLivePlanStatus,
  getRateCardLink,
  showNewSubLink,
} from 'helpers/users';
import withPurchase from 'decorators/withPurchase';
import withPreactivation from 'decorators/withPreactivation';
import withCredit from 'decorators/withCredit';
import Caret from 'images/icons/Caret';
import AnimateHeight from 'react-animate-height';
import { splitLongText } from 'helpers/text';
import { can, permActions } from 'permissions';
import { BUSINESS, RESELLER } from 'permissions/userGroups';
import { isBusinessChild } from '../../helpers/user';

const localStorageKey = '__openUserNav';

export class UserNav extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isOpen:
        !window.localStorage.getItem(localStorageKey) ||
        window.localStorage.getItem(localStorageKey) === 'true',
    };
  }

  componentDidMount() {
    document.body.style.overflowY = 'scroll';
  }

  componentWillUnmount() {
    document.body.style.overflowY = '';
  }

  handleToggleMenu = () => {
    const { isOpen } = this.state;
    this.setState({ isOpen: !isOpen });
    window.localStorage.setItem(localStorageKey, isOpen ? 'false' : 'true');
  };

  handleRateCardLinkClick = (e) => {
    const { user, history } = this.props;
    e.preventDefault();
    const link = getRateCardLink(user.id);
    if (link) {
      window.open(link, '_blank');
    } else {
      history.push(`/users/${user.id}/rate-card`);
    }
  };

  render() {
    const { user, loggedInUser } = this.props;
    const { isOpen } = this.state;

    if (!user) {
      return null;
    }

    const linkToLivePlanStatus = getLivePlanStatus(user);
    const isActive = user.is_active;
    const isBusinessChildUser = isBusinessChild(user);

    const hasAddress = user?.address?.length > 0;
    const canCreateSub =
      showNewSubLink(user) && isActive && !isBusinessChildUser;
    const canCreateSimo =
      canCreateSub && [BUSINESS, RESELLER].includes(user.acl_group_name);
    const showRateCard = user.acl_group_name === BUSINESS;
    const showActivePlan = user.acl_group_name === BUSINESS;
    const rateCardLink = getRateCardLink(user.id);
    const showPaymentMethods = !isBusinessChildUser;
    const showPurchases = !isBusinessChildUser;
    const showCredits = !isBusinessChildUser;
    const showPreactivations = !isBusinessChildUser;
    const showRollingPayments = !isBusinessChildUser;
    const links = [
      { to: `/users/${user.id}/view`, label: 'View User' },
      {
        to: `/users/${user.id}/edit`,
        label: 'Edit Account',
        permission: can(loggedInUser, permActions.EDIT_USER_PROFILE, {
          user,
          loggedInUser,
        }),
      },
      {
        to: `/users/${user.id}/credits`,
        label: 'View Credits',
        permission: can(loggedInUser, permActions.VIEW_USER_CREDIT),
        condition: showCredits,
      },
      {
        to: `/users/${user.id}/logs`,
        label: 'View Logs',
        permission: can(loggedInUser, permActions.VIEW_USER_LOGS),
      },
      {
        to: `/users/${user.id}/purchases`,
        label: 'View Purchases',
        condition: showPurchases,
        permission: can(loggedInUser, permActions.VIEW_PURCHASES),
      },
      {
        to: `/users/${user.id}/payment-methods`,
        label: 'Payment Methods',
        condition: showPaymentMethods,
        permission: can(loggedInUser, permActions.VIEW_PAYMENT_METHODS),
      },
      {
        to: `/users/${user.id}/preactivations`,
        label: 'Preactivations',
        permission: can(loggedInUser, permActions.VIEW_USER_PREACTIVATION),
        condition: showPreactivations,
      },
      {
        to: `/users/${user.id}/new-address`,
        label: 'New Address',
        condition: !hasAddress,
        permission: can(loggedInUser, permActions.CREATE_ADDRESS, {
          loggedInUser,
          userId: user.id,
        }),
      },
      {
        to: `/users/${user.id}/att/new-subscription`,
        label: 'Add AT&T Subscription',
        condition: canCreateSub,
      },
      {
        to: `/users/${user.id}/quick/new-subscription`,
        label: 'Add Quick Subscription',
        condition: canCreateSub,
      },
      {
        to: `/users/${user.id}/simo/new-subscription`,
        label: 'Add Simo Subscription',
        condition: canCreateSimo,
      },
      {
        to: `/users/${user.id}/simo/new-device`,
        label: 'Add Simo Device',
        condition: canCreateSimo,
      },
      {
        to: `/users/${user.id}/rolling-payments`,
        label: 'Rolling Payments',
        permission: can(loggedInUser, permActions.VIEW_USER_ROLLING_PAYMENTS),
        condition: showRollingPayments,
      },
      {
        to: `/users/${user.id}/login-devices`,
        label: 'Login Devices',
        permission: can(loggedInUser, permActions.VIEW_USER_LOGIN_DEVICES),
      },
      {
        to: linkToLivePlanStatus,
        label: 'Active Plans',
        condition: showActivePlan && linkToLivePlanStatus,
        external: true,
        target: '_blank',
      },
      {
        to: rateCardLink || `/users/${user.id}/rate-card`,
        label: 'Link To Your Rate Card',
        condition: showRateCard,
        permission: can(loggedInUser, permActions.VIEW_USER_RATE_CARD),
        external: Boolean(rateCardLink),
        target: rateCardLink ? '_blank' : '_self',
      },
      // {
      //   to: `/users/${user.id}/child-accounts`,
      //   label: 'Child Accounts',
      //   condition: showChildAccounts,
      // },
    ];

    return (
      <div className="col-xs-6 col-lg-12">
        <div>
          <br />
          <div
            className="mar-b-5"
            onClick={this.handleToggleMenu}
            style={{ cursor: 'pointer' }}
          >
            <strong>User ID: {user.id}</strong>
            <br />
            <div>
              <b style={{ position: 'relative' }}>
                {splitLongText(user.first_name)} {splitLongText(user.last_name)}
                <Caret
                  className={cn({
                    'main-nav-caret': true,
                    'main-nav-caret-user-nav': true,
                    'main-nav-caret-up': isOpen,
                  })}
                  id="user-nav-caret"
                />
              </b>
            </div>
          </div>
          <AnimateHeight duration={300} height={isOpen ? 'auto' : 0}>
            {links.map((link) => (
              <NavLink key={link.to} {...link} />
            ))}
          </AnimateHeight>
        </div>
      </div>
    );
  }
}

export const WithUserNav = withUser(UserNav);
export const WithPurchaseNav = withPurchase(UserNav);
export const WithPreactivationNav = withPreactivation(UserNav);
export const WithCreditNav = withCredit(UserNav);
